/**
 * The invoicing address a client can be treated as having chosen already.
 *
 * `InvoicingAddressSection` highlights a sole saved address at RENDER time only,
 * because §11.1 forbids seeding the store on mount. The step gates read the
 * store, so before this hook existed the form looked complete and Proceed
 * stayed dead — the client had to click the card that was already highlighted.
 *
 * The same shape of fix PM-D2 applied to the payment method: the caller derives
 * the default and every consumer judges `<store value> ?? <default>`. Both the
 * GATES (`selectBillingStepValidFor`, `selectSummaryStepValid`) and the WIRE
 * BODY (`serializePlacement`) must receive it — threading only the gates would
 * open Proceed and then make body assembly return null, which the place-order
 * mutation throws on. See the note on `selectBillingStepValidFor`.
 *
 * Deliberately narrow to EXACTLY ONE saved address. `InvoicingAddressDTO`
 * carries no BE `isDefault` marker (unlike payment methods' DF-B2 ladder), so
 * picking a winner among several would be a guess the backend gives no basis
 * for. With two or more, the client picks.
 *
 * Same query split as the section itself, so the two cannot disagree about
 * whose addresses these are: a client reads its own list, an admin ordering on
 * behalf reads the target user's.
 */

import { useMemo } from 'react'

import {
  useGetInvoicingAddressesByUserId,
  useGetInvoicingAddressesList
} from 'dataQueries/invoicingAddresses.query'
import type { AddressDTO } from 'models/purchaseFlowRevamp'

import { usePfStore } from '../../store'
import { dtoToSlice } from './sections/invoicingAddressSlice'

/**
 * @returns the sole saved address as a store-shaped slice, or `undefined` when
 * there are none, several, or the list has not loaded.
 *
 * @example
 * const defaultInvoicingAddress = useDefaultInvoicingAddress()
 * const isValid = selectBillingStepValidFor(defaultPaymentMethod, kinds, defaultInvoicingAddress)(state)
 */
export const useDefaultInvoicingAddress = (): AddressDTO | undefined => {
  const targetUserId = usePfStore((s) => s.targetUserId)

  const clientQuery = useGetInvoicingAddressesList(!targetUserId)
  const userScopedQuery = useGetInvoicingAddressesByUserId(targetUserId ?? undefined, !!targetUserId)
  const query = targetUserId ? userScopedQuery : clientQuery

  return useMemo(() => {
    const addresses = query.data ?? []
    if (addresses.length !== 1) return undefined

    const slice = dtoToSlice(addresses[0])
    // An address with no BE-issued id is the legacy `'new'` sentinel — one the
    // client started and never saved. It cannot be sent, so it is no default.
    return slice.id ? slice : undefined
  }, [query.data])
}
